import { useCallback, useState } from 'react'
import type { SyncTaskKey } from './hubspotSync.helpers'
import { useHubspotSyncChanges } from './useHubspotSyncChanges'
import { useHubspotSyncNewCompanies } from './useHubspotSyncNewCompanies'
import { useHubspotSyncReview } from './useHubspotSyncReview'
import { useHubspotSyncRun } from './useHubspotSyncRun'

/**
 * The whole HubSpot sync flow: the run, the task banner it leaves behind, and
 * which of the three decision modals is open. Each modal's own state lives in
 * its own hook; this only wires them to the run.
 */
export const useHubspotSync = () => {
  const run = useHubspotSyncRun()
  const [openTask, setOpenTask] = useState<SyncTaskKey | null>(null)
  const [isBannerDismissed, setBannerDismissed] = useState(false)
  const [isConfirmOpen, setConfirmOpen] = useState(false)

  const closeTask = useCallback(() => setOpenTask(null), [])

  const handleChangeApplied = useCallback(
    (clientBillingProfileId: number, emails: string[]) => {
      run.setRecipients(clientBillingProfileId, emails)
      run.resolveChange(clientBillingProfileId)
    },
    [run.setRecipients, run.resolveChange]
  )

  const handleNewCompaniesSubmitted = useCallback(
    (hubspotCompanyIds: string[]) => {
      run.resolveNewCompanies(hubspotCompanyIds)
      // With the last one set up the modal has nothing left to show, so it
      // closes itself, as Recipient updates does once it is applied.
      if (run.newItems.length <= hubspotCompanyIds.length) closeTask()
    },
    [run.resolveNewCompanies, run.newItems.length, closeTask]
  )

  const newCompanies = useHubspotSyncNewCompanies({
    items: run.newItems,
    runId: run.runId,
    onSubmitted: handleNewCompaniesSubmitted,
  })

  const changes = useHubspotSyncChanges({
    changes: run.changes,
    recipientsByProfileId: run.recipientsByProfileId,
    runId: run.runId,
    onApplied: handleChangeApplied,
    onClose: closeTask,
  })

  const review = useHubspotSyncReview({
    rows: run.reviewRecipients,
    recipientsByProfileId: run.recipientsByProfileId,
    runId: run.runId,
    onRemoved: run.setRecipients,
    onResolved: run.resolveReviewRows,
  })

  const handleSyncClick = useCallback(() => setConfirmOpen(true), [])

  const handleStartSync = useCallback(() => {
    setConfirmOpen(false)
    setBannerDismissed(false)
    setOpenTask(null)
    run.runSync()
  }, [run.runSync])

  return {
    run,
    newCompanies,
    changes,
    review,
    openTask,
    // The banner is the only trace of a finished run, so it stays until it is
    // dismissed or the next run replaces it.
    isBannerVisible: run.phase === 'ready' && !isBannerDismissed,
    // Needs review is a card on the page rather than a modal, so the banner's
    // View link only decides whether it is on screen.
    isReviewCardVisible:
      (openTask === 'review' || openTask === 'unsynced') &&
      (review.rows.length > 0 || run.reviewCompanies.length > 0),
    // One modal covers the pause and the run, so it stays open across both.
    isSyncModalOpen: isConfirmOpen || run.phase === 'running',
    handleSyncClick,
    handleStartSync,
    handleCancelSync: () => setConfirmOpen(false),
    handleOpenTask: setOpenTask,
    handleCloseTask: closeTask,
    handleDismissBanner: () => setBannerDismissed(true),
  }
}

export type HubspotSyncFlowState = ReturnType<typeof useHubspotSync>
